import React from "react";
import { Container, Typography, Box, styled, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import imagePathImg2 from "../assets/LayClif.PNG";

// --- Styled Components ---

const AboutWrapper = styled(Box)(({ theme }) => ({
    padding: theme.spacing(6, 0), // Tightened padding for better height control
    // Globally aware background
    backgroundColor: theme.palette.background.default === "#BBE0EF" ? "#BBE0EF" : "#FFFFFF",
    display: "flex",
    alignItems: "center",
    transition: "background-color 0.4s ease-in-out",
}));

const ExecutiveSection = styled(motion.div)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "60px",
    maxWidth: "1100px",
    margin: "0 auto",
    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        textAlign: "center",
        gap: "30px",
    },
}));

const ProfileImageBox = styled(Box)(({ theme }) => ({
    position: "relative",
    width: "280px",  // Slightly narrower for a more compact feel
    height: "380px", // Reduced height to prevent excessive scrolling
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    // The "Ring" / Background Frame
    "&::after": {
        content: '""',
        position: "absolute",
        width: "100%",
        height: "100%",
        border: "6px solid #B8860B",
        top: "12px",
        left: "12px",
        zIndex: 0,
        borderRadius: "20px",
    },
}));

const ExecutiveImage = styled("img")({
    width: "100%",
    height: "100%",
    borderRadius: "16px",
    zIndex: 1,
    position: "relative",
    objectFit: "cover",
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
});

const TextContent = styled(Box)({
    flex: "1",
    display: "flex",
    flexDirection: "column",
    maxWidth: "600px",
});

const NameTitle = styled(Typography)(({ theme }) => ({
    fontSize: "2.6rem",
    fontWeight: 900,
    // Dynamic color: Dark Navy on Sky Blue, Navy on White
    color: "#09122C",
    letterSpacing: "-0.03em",
    lineHeight: 1.1,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
        fontSize: "1.8rem",
    },
}));

const RoleBadge = styled(Typography)({
    fontSize: "0.8rem",
    fontWeight: 800,
    color: "#B8860B",
    textTransform: "uppercase",
    letterSpacing: "1.5px",
    marginBottom: "8px",
});

const BioText = styled(Typography)(({ theme }) => ({
    fontSize: "1rem",
    lineHeight: 1.6,
    // Text becomes darker/heavier on Sky Blue for contrast
    color: theme.palette.background.default === "#BBE0EF" ? "#000000" : "#4B5563",
    fontWeight: theme.palette.background.default === "#BBE0EF" ? 500 : 400,
}));

const AccentLine = styled(Box)({
    width: "50px",
    height: "4px",
    backgroundColor: "#B8860B",
    margin: "15px 0",
    borderRadius: "2px",
});

// --- Main Component ---

const About = () => {
    const theme = useTheme();
    const isSkyMode = theme.palette.background.default === "#BBE0EF";

    return (
        <AboutWrapper>
            <Container maxWidth="lg">
                <ExecutiveSection
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    {/* Compact Profile Ring */}
                    <ProfileImageBox>
                        <ExecutiveImage
                            src={imagePathImg2}
                            alt="Clifford Olayori - CEO"
                        />
                    </ProfileImageBox>

                    {/* Content Section */}
                    <TextContent>
                        <RoleBadge>Founder & CEO</RoleBadge>
                        <NameTitle variant="h1">
                            Clifford Olayori
                        </NameTitle>

                        <AccentLine />

                        <Box sx={{ textAlign: 'left' }}>
                            <BioText>
                                I am the founder and CEO of Crystal Career Center, leading with a vision to bridge
                                education and opportunity. With a degree in Business Administration, I’ve worked across
                                multiple sectors in the UK, Switzerland, and throughout Europe.
                            </BioText>
                            <BioText sx={{ mt: 2 }}>
                                I’m committed to building scalable solutions that empower learners and drive long-term growth across both public and private initiatives.
                            </BioText>
                        </Box>
                    </TextContent>
                </ExecutiveSection>
            </Container>
        </AboutWrapper>
    );
};

export default About;
